// Image export (F20). Framework-free: the dialog calls `exportImage`; tests call it through window.__marrow.
import type { Side, Viewer } from '../viewer/Viewer';
import { track } from '../analytics';
import { compose, toPng } from './compose';
import { PALETTES, fitSize, footerText, sizeFor, type Background, type ResolutionId } from './options';

export * from './options';

export type ExportRequest = {
  resolution: ResolutionId;
  background: Background;
  label: { text: string; id: string; side?: Side } | null;
  url: string;
};

export async function exportImage(viewer: Viewer, req: ExportRequest): Promise<{ blob: Blob; width: number; height: number; scaled: boolean; ms: number; phases: { render: number; finish: number } }> {
  const t0 = performance.now();
  const size = fitSize(sizeFor(req.resolution, viewer.visibleAspect()), viewer.maxImageSize());
  const point = req.label ? viewer.surfacePoint(req.label.id, req.label.side) : null;
  const bg = req.background === 'transparent' ? null : PALETTES[req.background].bg;
  const shot = viewer.renderImage({ width: size.width, height: size.height, background: bg, anchors: point ? [point] : [] });
  // Wait for the GPU here so the timings split cleanly into render (GPU-bound) and finishing (ours).
  shot.canvas.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, 1, 1);
  const t1 = performance.now();
  const anchor = shot.anchors[0];
  const canvas = compose({
    render: shot.canvas,
    background: req.background,
    footer: footerText(req.url),
    label: req.label && anchor ? { text: req.label.text, anchor } : null,
  });
  const blob = await toPng(canvas);
  const t2 = performance.now();
  const ms = Math.round(t2 - t0);
  track('export', { resolution: req.resolution, background: req.background, label: !!req.label, scaled: size.scaled });
  return { blob, width: canvas.width, height: canvas.height, scaled: size.scaled, ms, phases: { render: Math.round(t1 - t0), finish: Math.round(t2 - t1) } };
}
